import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import usePageTitle from "@/hooks/use-page-title";
import { RotateCw, ShoppingCart, Package } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/date";

export default function CustomerOrders() {
  usePageTitle("My Orders");
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState("");

  useEffect(() => {
    api.get("/orders").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);

  const reorder = async (o) => {
    setReordering(o.id);
    try {
      const { data } = await api.post(`/orders/${o.id}/reorder`);
      if (data.added_lines > 0) {
        toast.success(data.message + " — heading to cart…");
        setTimeout(() => nav("/cart"), 700);
      } else {
        toast.error("Nothing could be re-added — those products are no longer available.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reorder");
    } finally { setReordering(""); }
  };

  return (
    <div className="space-y-6" data-testid="my-orders-root">
      <div className="border-b border-[hsl(var(--border))] pb-6">
        <div className="overline mb-2">Customer</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">My Orders</h1>
        <p className="text-sm text-zinc-500 mt-1">One-tap reorder for any past order — we'll rebuild the cart exactly.</p>
      </div>
      <div className="card-swiss overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-4 py-3 text-left overline">Order #</th>
              <th className="px-4 py-3 text-left overline">Product</th>
              <th className="px-4 py-3 text-left overline">Date</th>
              <th className="px-4 py-3 text-right overline">Amount</th>
              <th className="px-4 py-3 text-left overline">Status</th>
              <th className="px-4 py-3 text-right overline">Reorder</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => {
              const canReorder = !!o.shop_order_id || (o.items || []).some((i) => i.catalog_product_id);
              return (
                <tr key={o.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]" data-testid={`my-order-${o.id}`}>
                  <td className="px-4 py-3"><Link to={`/orders/${o.id}`} className="font-mono text-xs font-semibold hover:text-[#FF6B00]">{o.id}</Link></td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold">{o.product_name || o.product_category}</div>
                    {(o.items || []).length > 1 && <div className="text-[10px] text-zinc-500">+{o.items.length - 1} more item{o.items.length > 2 ? "s" : ""}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{Math.round(o.estimated_cost || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {canReorder ? (
                      <button
                        onClick={() => reorder(o)}
                        disabled={reordering === o.id}
                        className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border-2 border-[#FF6B00] text-[#FF6B00] hover:bg-[#FF6B00] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`reorder-btn-${o.id}`}
                      >
                        {reordering === o.id ? <RotateCw size={12} className="animate-spin" /> : <RotateCw size={12} />}
                        {reordering === o.id ? "Adding…" : "Reorder"}
                      </button>
                    ) : (
                      <span className="text-[10px] text-zinc-400 italic">Manual order</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="py-16 text-center">
                <Package size={26} className="mx-auto text-zinc-300 mb-2" />
                <div className="text-sm text-zinc-500">No orders yet.</div>
                <Link to="/products" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#FF6B00] hover:underline">
                  <ShoppingCart size={12} /> Browse products
                </Link>
              </td></tr>
            )}
            {loading && (<tr><td colSpan={6} className="py-10 text-center text-zinc-400 text-sm">Loading…</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
